import React, {useCallback, useRef, useState} from 'react';
import AIMessageGenerator from '../aiMessageGenerator/AIMessageGenerator';
import type {
    AIMessageGeneratorAssistantFooterProps,
    AIMessageGeneratorEmptyStateProps,
    AIMessageGeneratorPromptAreaProps,
    AIMessageGeneratorUserMessageProps,
} from '../aiMessageGenerator/utils/types';
import AiAgentFab from './components/aiAgentFab/AiAgentFab';
import AiAgentEmptyState from './components/aiAgentEmptyState/AiAgentEmptyState';
import AiAgentSuggestions from './components/aiAgentSuggestions/AiAgentSuggestions';
import AiAgentUserMessage from './components/aiAgentUserMessage/AiAgentUserMessage';
import AiAgentPromptArea from './components/aiAgentPromptArea/AiAgentPromptArea';
import AiAgentProposalConfirm from './components/aiAgentProposalConfirm/AiAgentProposalConfirm';
import AiAgentFeedback from './components/aiAgentFeedback/AiAgentFeedback';
import AiAgentGoToPage from './components/aiAgentGoToPage/AiAgentGoToPage';
import {useAiAgentPanel} from './hooks/useAiAgentPanel';

const noFilePicker = () => {};

function AiAgent() {
    const {
        t,
        agentShow,
        openAgent,
        handleReset,
        handleSubmit,
        pendingAsk,
        clearAsk,
        pendingProposal,
        pendingFeedback,
        createdRecord,
        openCreatedRecord,
        turnXHR,
    } = useAiAgentPanel();
    const openFilePickerRef = useRef<() => void>(noFilePicker);
    const [promptHint, setPromptHint] = useState<string | null>(null);

    const clearPromptHint = useCallback(() => setPromptHint(null), []);

    // The hint describes the suggestion that filled the box, so it belongs to that draft only.
    const handleResetChat = useCallback(() => {
        clearPromptHint();
        handleReset();
    }, [clearPromptHint, handleReset]);

    const renderEmptyState = useCallback(({onSuggestionFill}: AIMessageGeneratorEmptyStateProps) => (
        <AiAgentEmptyState>
            <AiAgentSuggestions
                onSuggestionFill={onSuggestionFill}
                onOpenFilePicker={() => openFilePickerRef.current()}
                onPromptHintChange={setPromptHint}
            />
        </AiAgentEmptyState>
    ), []);

    const renderPromptArea = useCallback((props: AIMessageGeneratorPromptAreaProps) => (
        <AiAgentPromptArea
            {...props}
            pendingAsk={pendingAsk}
            onClearAsk={clearAsk}
            openFilePickerRef={openFilePickerRef}
            promptHint={promptHint}
            onClearPromptHint={clearPromptHint}
        />
    ), [pendingAsk, clearAsk, promptHint, clearPromptHint]);

    // The bubble already carries the details — the summary's list, or the open form beside an
    // early offer — so the action lives in it instead of repeating them in a card of its own.
    // Once the record exists, the same slot offers the way to it ("go to page") and asks how
    // the chat went.
    const renderAssistantFooter = useCallback(({isLast, draftLoading, onSubmitPrompt}: AIMessageGeneratorAssistantFooterProps) => {
        if(!isLast) {
            return null;
        }
        if(pendingProposal) {
            return (
                <AiAgentProposalConfirm
                    proposal={pendingProposal}
                    disabled={draftLoading}
                    onSubmitPrompt={onSubmitPrompt}
                />
            );
        }
        if(!createdRecord && !pendingFeedback) {
            return null;
        }
        return (
            <>
                {createdRecord && (
                    <AiAgentGoToPage
                        record={createdRecord}
                        disabled={draftLoading}
                        onOpen={openCreatedRecord}
                    />
                )}
                {pendingFeedback && <AiAgentFeedback feedback={pendingFeedback} />}
            </>
        );
    }, [pendingProposal, pendingFeedback, createdRecord, openCreatedRecord]);

    const renderUserMessage = useCallback(({message, index, onEditMessage, draftLoading}: AIMessageGeneratorUserMessageProps) => (
        <AiAgentUserMessage
            message={message}
            index={index}
            onEditMessage={onEditMessage}
            draftLoading={draftLoading}
        />
    ), []);

    return (
        <>
            <AiAgentFab onClick={openAgent} />
            <AIMessageGenerator
                show={agentShow}
                title={t('title')}
                placeholder={t('inputPlaceholder')}
                newChatLabel={t('newChat')}
                errorMessage={t('errorMessage')}
                suggestions={[]}
                submit={{label: t('send'), onClick: handleSubmit, xhrState: turnXHR}}
                renderEmptyState={renderEmptyState}
                renderPromptArea={renderPromptArea}
                renderUserMessage={renderUserMessage}
                renderAssistantFooter={renderAssistantFooter}
                onReset={handleResetChat}
                closeOnOutsideClick={false}
                hideBackdrop
                resetOnClose={false}
                dataCy="ai-agent"
            />
        </>
    );
}

export default AiAgent;
